type ReloadPhase = 'authorize' | 'bootstrap' | 'logout'

type ProtectedFlag = ReturnType<typeof computed<boolean>>
type GateUidValue = ReturnType<typeof computed<string | null | undefined>>

interface UseProtectedPageGateOptions {
    isProtected: ProtectedFlag
    gateUid: GateUidValue
    reloadEntry: () => Promise<void>
    onReloadError?: (error: unknown, phase: ReloadPhase) => void
}

const GATE_TOKEN_QUERY_PARAM = 'gate_token'
const GATE_UID_QUERY_PARAM = 'gate_uid'

interface CallbackParams {
    token: string | null
    gateUid: string | null
    cleanHash: string | undefined
}

export function parseProtectedPageGateCallbackHash(rawHash: string): CallbackParams {
    const normalizedHash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash

    if (!normalizedHash) {
        return {
            token: null,
            gateUid: null,
            cleanHash: undefined,
        }
    }

    let fragmentPath = ''
    let paramsString = ''

    const queryIndex = normalizedHash.indexOf('?')
    if (queryIndex >= 0) {
        fragmentPath = normalizedHash.slice(0, queryIndex)
        paramsString = normalizedHash.slice(queryIndex + 1)
    } else if (normalizedHash.includes('=')) {
        paramsString = normalizedHash
    } else {
        return {
            token: null,
            gateUid: null,
            cleanHash: rawHash || undefined,
        }
    }

    const params = new URLSearchParams(paramsString)
    const token = params.get(GATE_TOKEN_QUERY_PARAM)
    const gateUid = params.get(GATE_UID_QUERY_PARAM)

    if (!token && !gateUid) {
        return {
            token: null,
            gateUid: null,
            cleanHash: rawHash || undefined,
        }
    }

    params.delete(GATE_TOKEN_QUERY_PARAM)
    params.delete(GATE_UID_QUERY_PARAM)

    const remainingParams = params.toString()
    const cleanHashValue = remainingParams
        ? `${fragmentPath ? `${fragmentPath}?` : ''}${remainingParams}`
        : fragmentPath

    return {
        token,
        gateUid,
        cleanHash: cleanHashValue ? `#${cleanHashValue}` : undefined,
    }
}

export function useProtectedPageGate(options: UseProtectedPageGateOptions) {
    const route = useRoute()
    const router = useRouter()
    const { validateToken } = useProtectedContent()

    const tokenCheckComplete = ref(false)
    const isReloadingAfterAuth = ref(false)
    const isReloadingAfterLogout = ref(false)

    const isReloading = computed(() => isReloadingAfterAuth.value || isReloadingAfterLogout.value)

    const showLoadingState = computed(() => {
        if (isReloading.value) {
            return true
        }

        if (tokenCheckComplete.value) {
            return false
        }

        return options.isProtected.value
    })

    const showGate = computed(() => {
        return !isReloading.value && options.isProtected.value && tokenCheckComplete.value
    })

    const reloadEntry = async (phase: ReloadPhase) => {
        try {
            await options.reloadEntry()
        } catch (error) {
            options.onReloadError?.(error, phase)
        }
    }

    const getSingleQueryValue = (value: unknown): string | null => {
        if (typeof value === 'string' && value !== '') {
            return value
        }

        if (Array.isArray(value) && typeof value[0] === 'string' && value[0] !== '') {
            return value[0]
        }

        return null
    }

    const getCallbackGateUid = (): string | null => {
        return getSingleQueryValue(route.query[GATE_UID_QUERY_PARAM])
    }

    const getCallbackParamsFromHash = (): CallbackParams => {
        if (!import.meta.client) {
            return {
                token: null,
                gateUid: null,
                cleanHash: undefined,
            }
        }

        return parseProtectedPageGateCallbackHash(window.location.hash)
    }

    const removeCallbackParams = async (cleanHash?: string) => {
        const {
            [GATE_TOKEN_QUERY_PARAM]: _removedGateToken,
            [GATE_UID_QUERY_PARAM]: _removedGateUid,
            ...cleanQuery
        } = route.query

        await router.replace({
            path: route.path,
            query: cleanQuery,
            hash: cleanHash,
        })
    }

    const handleAuthorized = async () => {
        isReloadingAfterAuth.value = true

        try {
            await reloadEntry('authorize')
        } finally {
            isReloadingAfterAuth.value = false
        }
    }

    const handleLoggedOut = async () => {
        isReloadingAfterLogout.value = true

        try {
            await reloadEntry('logout')
        } finally {
            isReloadingAfterLogout.value = false
        }
    }

    onMounted(async () => {
        const hashCallback = getCallbackParamsFromHash()
        const callbackToken = hashCallback.token ?? getSingleQueryValue(route.query[GATE_TOKEN_QUERY_PARAM])
        const callbackGateUid = hashCallback.gateUid ?? getCallbackGateUid()

        if (callbackToken && callbackGateUid) {
            setStoredGateToken(callbackGateUid, callbackToken)

            await removeCallbackParams(hashCallback.cleanHash ?? (route.hash || undefined))
            await reloadEntry('bootstrap')

            tokenCheckComplete.value = true
            return
        }

        if (!options.isProtected.value) {
            tokenCheckComplete.value = true
            return
        }

        const gateUid = options.gateUid.value

        if (gateUid) {
            const hasValidToken = await validateToken(gateUid)

            if (hasValidToken) {
                await reloadEntry('bootstrap')
            }

            tokenCheckComplete.value = true
            return
        }

        tokenCheckComplete.value = true
    })

    return {
        tokenCheckComplete: readonly(tokenCheckComplete),
        isReloadingAfterAuth: readonly(isReloadingAfterAuth),
        isReloadingAfterLogout: readonly(isReloadingAfterLogout),
        showLoadingState,
        showGate,
        handleAuthorized,
        handleLoggedOut,
    }
}
